import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Phone, MapPin, Mail, Clock } from "lucide-react";
import { Header } from "@/components/landing/Header";
import { Footer } from "@/components/landing/Footer";
import { Reveal, Eyebrow } from "@/components/landing/Reveal";
import { PatternLayer, patternLight } from "@/components/landing/patterns";
import { SITE } from "@/lib/site";
import { useSEO } from "@/lib/seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TOPICS = [
  "Selling gold",
  "Selling a watch",
  "Selling diamonds",
  "Ring sizing",
  "Custom design",
  "Jewelry or watch repair",
  "General question",
];

const LOCATION_EMAILS = {
  "Katy Showroom": SITE.emails.katy,
  "Copperas Cove Showroom": SITE.emails.cove,
  "Killeen Showroom": SITE.emails.killeen,
};

export default function ContactPage() {
  useSEO({
    title: "Contact Gold Time Jewelry | Katy, Copperas Cove & Killeen TX",
    canonicalPath: "/contact",
    description: "Contact Gold Time Jewelry — call, WhatsApp or visit our showrooms in Katy, Copperas Cove and Killeen, Texas. Family owned since 1991.",
  });
  const [searchParams] = useSearchParams();
  const item = searchParams.get("item");
  const topicParam = searchParams.get("topic");
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    interest: TOPICS.includes(topicParam) ? topicParam : "",
    message: item ? `About item SKU ${item}: ` : "",
  });
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.message) {
      toast.error("Please fill in your name, phone and message.");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/enquiries`, {
        ...form,
        interest: form.interest || null,
        email: form.email || null,
      });
      toast.success("Thank you — we'll be in touch shortly.");
      setForm({ name: "", phone: "", email: "", interest: "", message: "" });
    } catch {
      toast.error("Something went wrong — please call or email us instead.");
    }
    setSending(false);
  };

  const inputCls =
    "w-full bg-white border border-[#E8DCC4] rounded-sm px-4 py-3 text-base text-neutral-900 focus:outline-none focus:border-[#C9A227] focus:ring-1 focus:ring-[#C9A227] transition-colors duration-300";

  return (
    <main data-testid="contact-page">
      <Header />
      <section className="relative overflow-hidden pt-[164px] pb-24 lg:pt-[196px] lg:pb-32">
        <PatternLayer pattern={patternLight} />
        <div className="relative max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <Reveal>
            <Eyebrow>We're here to help</Eyebrow>
            <h1 className="font-display text-4xl md:text-5xl font-light tracking-tight text-neutral-900" data-testid="contact-heading">
              Contact <span className="text-[#C9A227]">Gold Time</span>
            </h1>
            <p className="mt-6 text-base md:text-lg text-neutral-600 leading-relaxed max-w-2xl">
              Valuations, sizing, repairs, custom work, or a question about a piece — send us the details
              and the right showroom will get back to you, usually the same business day.
            </p>
          </Reveal>

          <div className="mt-14 grid lg:grid-cols-2 gap-10 items-start">
            <Reveal delay={0.1}>
              <form onSubmit={submit} className="bg-white border border-[#E8DCC4] rounded-sm p-7 space-y-4" data-testid="contact-form">
                <div className="grid sm:grid-cols-2 gap-4">
                  <input
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="Your name"
                    data-testid="contact-name"
                    className={inputCls}
                  />
                  <input
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    placeholder="Phone"
                    data-testid="contact-phone"
                    className={inputCls}
                  />
                </div>
                <input
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="Email (optional)"
                  type="email"
                  data-testid="contact-email"
                  className={inputCls}
                />
                <select
                  value={form.interest}
                  onChange={(e) => setForm({ ...form, interest: e.target.value })}
                  data-testid="contact-topic"
                  className={inputCls}
                >
                  <option value="">This is about…</option>
                  {TOPICS.map((t) => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
                <textarea
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  placeholder="Tell us what you have in mind — include an item SKU if it's about a piece"
                  rows={5}
                  data-testid="contact-message"
                  className={inputCls}
                />
                <button
                  type="submit"
                  disabled={sending}
                  data-testid="contact-submit"
                  className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-[#E8CC6E] to-[#C9A227] text-neutral-900 text-sm font-semibold px-8 py-3.5 hover:from-[#F0D988] hover:to-[#D4AF37] hover:-translate-y-0.5 transition-[transform] duration-300 shadow-[0_16px_32px_rgb(201,162,39,0.20)] disabled:opacity-60"
                >
                  {sending ? "Sending…" : "Send message"}
                </button>
                <p className="text-xs text-neutral-400 text-center">
                  Prefer email? Write to us at {SITE.emails.main}
                </p>
              </form>
            </Reveal>

            <div className="space-y-5">
              {SITE.locations.map((loc, i) => (
                <Reveal key={loc.name} delay={0.12 + i * 0.06}>
                  <div className="bg-white border border-[#E8DCC4] rounded-sm p-6" data-testid={`contact-showroom-${i}`}>
                    <h3 className="font-display text-xl text-neutral-900">{loc.name}</h3>
                    <div className="mt-3 space-y-2 text-sm text-neutral-600">
                      <p className="flex items-start gap-2.5">
                        <MapPin size={15} strokeWidth={1.5} className="text-[#C9A227] mt-0.5 shrink-0" />
                        <a href={loc.mapsUrl} target="_blank" rel="noopener noreferrer" className="hover:text-[#A8871B] transition-colors duration-300">
                          {loc.lines.join(", ")}
                        </a>
                      </p>
                      <p className="flex items-center gap-2.5">
                        <Phone size={15} strokeWidth={1.5} className="text-[#C9A227] shrink-0" />
                        <a href={loc.phoneHref} className="hover:text-[#A8871B] transition-colors duration-300" data-testid={`contact-phone-${i}`}>
                          {loc.phone}
                        </a>
                      </p>
                      <p className="flex items-center gap-2.5">
                        <Mail size={15} strokeWidth={1.5} className="text-[#C9A227] shrink-0" />
                        <a href={`mailto:${LOCATION_EMAILS[loc.name]}`} className="hover:text-[#A8871B] transition-colors duration-300" data-testid={`contact-email-${i}`}>
                          {LOCATION_EMAILS[loc.name]}
                        </a>
                      </p>
                      <p className="flex items-center gap-2.5">
                        <Clock size={15} strokeWidth={1.5} className="text-[#C9A227] shrink-0" />
                        Mon–Sat 10am–7pm · Sun 12–6pm
                      </p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>
      <Footer />
    </main>
  );
}
